import { ENetwork } from '@NGUtils/enums';
import { removeLocalStorage, removeLocalStorageWithKeyStartingBy } from '@functions/localStorage';
import { BigNumber, Contract, ContractInterface, Signer, Wallet, ethers, providers, utils } from 'ethers';
import { RpcProvider } from 'starknet';
import { EWalletType } from './web3Store.enums';
import { TDefaultProvider, TWallets, TWeb3Account } from './web3Store.type';
import { NETWORKS_DATA, WALLETS_ARRAY } from './web3Store.variables';

function convertBigNumberToString(value?: BigNumber, decimals?: number): string {
	if (!value) return '0';
	if (!decimals) return value.toString();

	return ethers.utils.formatUnits(value, decimals);
}

function getWalletFromId(id: string): TWallets | undefined {
	return WALLETS_ARRAY.find((w: TWallets) => w.id === id);
}

function getContract(address: string, abi: ContractInterface, signerOrProvider: Signer | providers.Provider): Contract {
	// check if address is valid
	if (!utils.isAddress(address)) {
		throw new Error('Invalid address');
	}

	// check if abi is valid
	try {
		new utils.Interface(JSON.stringify(abi));
	} catch (error) {
		throw new Error('Invalid ABI');
	}

	return new Contract(address, abi, signerOrProvider);
}

function getDefaultSigner<T extends EWalletType>(type: T): Signer | RpcProvider | null {
	switch (type) {
		case EWalletType.ethereum: {
			const provider = new providers.JsonRpcProvider(NETWORKS_DATA.sepolia.rpc);

			return new Wallet(process.env.DEFAULT_SIGNER_PK || '', provider);
		}
		case EWalletType.starknet: {
			throw new Error('getDefaultSigner - starknet: Not implemented');
		}
		default:
			break;
	}

	return null;
}

function getDefaultProvider<T extends EWalletType>(type: T): TDefaultProvider[T] | null {
	try {
		switch (type) {
			case EWalletType.ethereum: {
				return new providers.JsonRpcProvider(NETWORKS_DATA[ENetwork.sepolia].rpc) as TDefaultProvider[T];
			}
			case EWalletType.starknet: {
				const options = {
					nodeUrl: NETWORKS_DATA[ENetwork.starknetSepolia].rpc
				};

				return new RpcProvider(options) as TDefaultProvider[T];
			}
			default:
				break;
		}
	} catch (err) {
		console.error('getDefaultProvider: ', err);
	}

	return null;
}

function addWeb3Account(accounts: TWeb3Account[], newAccount: TWeb3Account) {
	const accountIndex = accounts.findIndex((account) => account.id === newAccount.id);

	if (accountIndex === -1) {
		return [...accounts, newAccount];
	} else {
		// Account already exists but we refresh it to subscribe to new events

		// replace account with new one
		const updatedAccounts = [...accounts];
		updatedAccounts[accountIndex] = newAccount;

		return updatedAccounts;
	}

	return accounts;
}

const updateWeb3Account = (
	accounts: TWeb3Account[],
	accountToUpdateId: TWeb3Account['id'],
	updatedAccountProperties: Partial<Omit<TWeb3Account, 'id'>>
) => {
	const accountIndex = accounts.findIndex((account) => account.id === accountToUpdateId);

	if (accountIndex >= 0) {
		// Account exists, update it
		const updatedAccounts = [...accounts];
		updatedAccounts[accountIndex] = {
			...updatedAccounts[accountIndex],
			...updatedAccountProperties
		};

		return updatedAccounts;
	} else {
		console.error('updateWeb3Account: Account does not exist');
	}

	return accounts;
};

function resetWalletProvidersLocalStorage() {
	removeLocalStorageWithKeyStartingBy('wc@');
	removeLocalStorage('WCM_VERSION');
}

export {
	addWeb3Account,
	convertBigNumberToString,
	getContract,
	getDefaultProvider,
	getDefaultSigner,
	getWalletFromId,
	resetWalletProvidersLocalStorage,
	updateWeb3Account
};
